import { PublicKey } from "@solana/web3.js";

import {
  buildRelayPayload,
  fetchCommitments,
  fetchPlatformFee,
  submitToRelayer,
  type RelayPayload,
} from "../arcaneProgram";
import { getRiskApiUrlForNetwork } from "../config/constants";
import { getConfig } from "../config/runtime";
import {
  convertProofToBytes,
  generateWithdrawProof,
  parseSecretNote,
} from "../zkProof";
import {
  getIndexerAssetFromResolved,
  getIndexerDenomination,
  resolveNoteAsset,
} from "./assets";
import { assertWithdrawRecipientAllowed } from "./risk";
import {
  calculateWithdrawFee,
  pollWithdrawJob,
  selectRelayer,
} from "./relayer";

const WITHDRAW_MAX_WAIT_MS = 60_000;

export type WithdrawParams = {
  /** Secret note from the original deposit. */
  note: string;
  /** Withdrawal recipient public key. */
  recipient: PublicKey | string;
  /** Mode for normal withdraw or arcane transfer. */
  arcaneTransferMode?: boolean;
};

export type WithdrawStatus = "success" | "failed";

export type WithdrawResult = {
  txHash: string;
  status: WithdrawStatus;
};

export type WithdrawRelayBundle = {
  payload: RelayPayload;
  relayerUrl: string;
};

export async function buildWithdrawPayload(
  params: WithdrawParams,
  notes4Proof: string[] = [],
  relayerIdx: number = -1
): Promise<WithdrawRelayBundle> {
  const { network, relayers } = getConfig();
  const selectedRelayer = selectRelayer(relayers, relayerIdx === -1 ? undefined : relayerIdx);

  const recipient =
    params.recipient instanceof PublicKey
      ? params.recipient.toBase58()
      : params.recipient.trim();

  await assertWithdrawRecipientAllowed(
    recipient,
    getRiskApiUrlForNetwork(network)
  );

  const parsed = parseSecretNote(params.note);
  const { amount, nullifier, secret, commitment, nullifierHash, mint } = parsed;

  if (!amount || !nullifier || !secret || !commitment || !nullifierHash) {
    throw new Error("Invalid note format");
  }

  const asset = resolveNoteAsset(network, amount, mint ?? null);
  const depositParams = {
    amount,
    nullifier,
    secret,
    commitment,
    nullifierHash,
    mint,
  };
  const denomination = getIndexerDenomination(amount, asset);
  const indexerAsset = getIndexerAssetFromResolved(asset);
  let commitments: string[] = (await fetchCommitments(
    denomination,
    indexerAsset
  )) as string[];
  if (!Array.isArray(commitments)) {
    commitments = [commitments as unknown as string];
  }

  const relayMint = mint ?? asset.mint;
  const platformFeePercent = await fetchPlatformFee();
  const fee = calculateWithdrawFee(amount, selectedRelayer.fee, platformFeePercent);
  const refund = 0n;

  const decimalStrings = commitments.map((hex) => {
    return BigInt(hex).toString()
  });

  if (params.arcaneTransferMode && notes4Proof.length > 0) {
    notes4Proof.forEach(note => {
      const parsed = parseSecretNote(note)
      if (parsed.commitment && amount === parsed.amount && mint === parsed.mint) {
        decimalStrings.push(parsed.commitment)
      }
    })
  }

  const { circuitPaths } = getConfig();
  const { proof, args } = (await generateWithdrawProof(
    depositParams,
    decimalStrings,
    new PublicKey(recipient),
    new PublicKey(selectedRelayer.value),
    fee,
    refund,
    circuitPaths
      ? {
          circuitWasmPath: circuitPaths.wasm,
          circuitZkeyPath: circuitPaths.zkey,
        }
      : undefined
  )) as {
    proof: unknown;
    args: unknown[];
  };

  const proofBytes = await convertProofToBytes(proof);
  const proofHex = Array.from(proofBytes)
    .slice(0, 256)
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");

  const payload = buildRelayPayload({
    args,
    proofHex,
    denomination: depositParams.amount,
    withdrawAddress: recipient,
    relayerPubkey: selectedRelayer.value,
    mint: relayMint,
  });

  return {
    payload,
    relayerUrl: selectedRelayer.url,
  };
}

export async function submitWithdrawPayload(
  bundle: WithdrawRelayBundle
): Promise<WithdrawResult> {
  const submitResult = (await submitToRelayer(
    bundle.relayerUrl,
    bundle.payload
  )) as { id?: string };
  const jobId = submitResult.id;
  if (!jobId) {
    throw new Error("Relayer did not return a job id");
  }

  const { txHash, status } = await pollWithdrawJob(
    bundle.relayerUrl,
    jobId,
    WITHDRAW_MAX_WAIT_MS
  );

  return {
    txHash,
    status,
  };
}

export async function executeWithdraw(
  params: WithdrawParams,
  relayerIdx: number = -1
): Promise<WithdrawResult> {
  const bundle = await buildWithdrawPayload(params, [], relayerIdx);
  return submitWithdrawPayload(bundle);
}
